import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl) {
  throw new Error('Missing VITE_SUPABASE_URL environment variable. Please check your .env file.')
}

if (!supabaseKey) {
  throw new Error('Missing VITE_SUPABASE_ANON_KEY environment variable. Please check your .env file.')
}

console.log('🔧 Supabase configuration:')
console.log('- URL:', supabaseUrl)
console.log('- API Key present:', supabaseKey ? 'Yes (****)' : 'No')
console.log('- Environment:', import.meta.env.MODE || 'unknown')

export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    // Simplified storage with better error handling
    storage: {
      getItem: (key: string) => {
        try {
          if (typeof window !== 'undefined') {
            const item = window.localStorage.getItem(key)
            
            // Validate session data without excessive logging
            if (item && key.includes('auth-token')) {
              try {
                const parsed = JSON.parse(item)
                if (parsed && typeof parsed === 'object') {
                  // Clean invalid refresh tokens
                  if (!parsed.refresh_token || parsed.refresh_token === 'undefined') {
                    window.localStorage.removeItem(key)
                    return null
                  }
                }
              } catch (e) {
                // Clear corrupted data silently
                window.localStorage.removeItem(key)
                return null
              }
            }
            
            return item
          }
        } catch (error) {
          console.error('Storage getItem error:', error)
        }
        return null
      },
      setItem: (key: string, value: string) => {
        try {
          if (typeof window !== 'undefined') {
            // Validate session data before storing
            if (key.includes('auth-token') && value) {
              try {
                const parsed = JSON.parse(value)
                if (!parsed.refresh_token || parsed.refresh_token === 'undefined') {
                  return // Don't store invalid sessions
                }
              } catch (e) {
                return // Don't store invalid data
              }
            }
            
            window.localStorage.setItem(key, value)
          }
        } catch (error) {
          console.error('Storage setItem error:', error)
        }
      },
      removeItem: (key: string) => {
        try {
          if (typeof window !== 'undefined') {
            window.localStorage.removeItem(key)
          }
        } catch (error) {
          console.error('Storage removeItem error:', error)
        }
      }
    },
    // Debug only in development
    debug: false // Disable verbose logging even in dev for cleaner output
  },
  // Simplified global configuration - let Supabase handle headers automatically
  global: {
    // Don't set default headers here - let Supabase client manage them
    // This allows proper session tokens to be used when authenticated
    // Custom fetch with minimal logging for 401 errors only
    fetch: async (url: any, options: any = {}) => {
      // Ensure we have proper headers for JSON requests
      const headers = new Headers(options.headers || {})
      
      // Always set apikey if not present
      if (!headers.has('apikey')) {
        headers.set('apikey', supabaseKey)
      }
      
      // IMPORTANT: Don't override Authorization header if it's already set
      // The Supabase client will set the proper session token when authenticated
      // Only set default Authorization if completely missing
      if (!headers.has('Authorization') && !headers.has('authorization')) {
        headers.set('Authorization', `Bearer ${supabaseKey}`)
      }
      
      // For requests with body, ensure Content-Type is set correctly
      if (options.body) {
        // If body is FormData, let browser set Content-Type with boundary
        if (!(options.body instanceof FormData)) {
          // For JSON requests, ensure Content-Type is application/json
          if (!headers.has('Content-Type')) {
            headers.set('Content-Type', 'application/json')
          }
        }
      }
      
      // Always accept JSON responses
      if (!headers.has('Accept')) {
        headers.set('Accept', 'application/json')
      }
      
      try {
        const response = await fetch(url, { 
          ...options, 
          headers 
        })
        
        // Only log actual auth errors
        if (response.status === 401) {
          console.error('Authentication error (401) for:', url.toString().replace(supabaseKey, '***'))
          
          // Try to get error details
          try {
            const errorBody = await response.clone().json()
            console.error('Error details:', errorBody)
          } catch {
            // Ignore JSON parse errors
          }
        }
        
        // Log Content-Type errors
        if (response.status === 415 || response.status === 406) {
          console.error(`Content-Type error (${response.status}) for:`, url.toString().replace(supabaseKey, '***'))
          console.error('Request headers:', Object.fromEntries(headers.entries()))
          
          try {
            const errorBody = await response.clone().text()
            console.error('Error response:', errorBody)
          } catch {
            // Ignore parse errors
          }
        }
        
        return response
      } catch (error) {
        console.error('Network error:', error)
        throw error
      }
    }
  },
  // Database schema configuration with optimizations
  db: {
    schema: 'public'
  },
  // Optimized real-time configuration
  realtime: {
    params: {
      eventsPerSecond: 10
    },
    // Connection timeout and retry settings
    timeout: 10000,
    heartbeatIntervalMs: 30000,
    reconnectAfterMs: (tries: number) => Math.min(tries * 1000, 30000),
    // Enable compression for real-time data
    encode: (payload: any) => JSON.stringify(payload),
    decode: (payload: string) => JSON.parse(payload)
  }
})

// Session management with improved error handling
export const getAuthenticatedSupabase = async () => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession()
    
    if (error) {
      // Handle different error types
      if (error.message?.includes('NetworkError') || error.message?.includes('Failed to fetch')) {
        throw new Error('Network error - please check your connection')
      }
      
      if (error.status === 400 || error.status === 401 || error.message?.includes('refresh_token')) {
        // Clear invalid session
        await supabase.auth.signOut()
        throw new Error('Session expired - please sign in again')
      }
      
      throw error
    }
    
    if (!session) {
      throw new Error('No active session - please sign in')
    }
    
    // Check if token needs refresh (expires within 5 minutes)
    const now = Math.floor(Date.now() / 1000)
    const expiresAt = session.expires_at || 0
    
    if (expiresAt - now < 300) {
      try {
        const { data: { session: newSession }, error: refreshError } = await supabase.auth.refreshSession({
          refresh_token: session.refresh_token
        })
        
        if (refreshError || !newSession) {
          // If refresh fails, clear session and require re-auth
          await supabase.auth.signOut()
          throw new Error('Session expired - please sign in again')
        }
        
        return { supabase, session: newSession }
      } catch {
        // If we can't refresh but session is still valid, use it
        if (expiresAt > now) {
          return { supabase, session }
        }
        
        await supabase.auth.signOut()
        throw new Error('Session expired - please sign in again')
      }
    }
    
    return { supabase, session }
  } catch (error) {
    console.error('Authentication error:', error)
    throw error
  }
}

// Helper for authenticated operations
export const withAuth = async <T>(
  operation: (client: typeof supabase, session: any) => Promise<T>
): Promise<T> => {
  const { data: { session }, error } = await supabase.auth.getSession()
  
  if (error || !session) {
    throw new Error('Authentication required')
  }
  
  return operation(supabase, session)
}

// Database types for TypeScript (updated with RBAC schema)
export type Json =
  | string
  | number
  | boolean
  | null
  | { [key: string]: Json | undefined }
  | Json[]

export type Database = {
  // Allows to automatically instanciate createClient with right options
  // instead of createClient<Database, { PostgrestVersion: 'XX' }>(URL, KEY)
  __InternalSupabase: {
    PostgrestVersion: "12.2.12 (cd3cf9e)"
  }
  public: {
    Tables: {
      admin_activity_logs: {
        Row: {
          action_type: string
          created_at: string | null
          details: Json | null
          error_message: string | null
          id: string
          ip_address: unknown | null
          risk_level: string | null
          session_id: string | null
          success: boolean | null
          target_id: string | null
          target_type: string | null
          user_agent: string | null
        }
        Insert: {
          action_type: string
          created_at?: string | null
          details?: Json | null
          error_message?: string | null
          id: string
          ip_address?: unknown | null
          risk_level?: string | null
          session_id?: string | null
          success?: boolean | null
          target_id?: string | null
          target_type?: string | null
          user_agent?: string | null
        }
        Update: {
          action_type?: string
          created_at?: string | null
          details?: Json | null
          error_message?: string | null
          id?: string
          ip_address?: unknown | null
          risk_level?: string | null
          session_id?: string | null
          success?: boolean | null
          target_id?: string | null
          target_type?: string | null
          user_agent?: string | null
        }
        Relationships: []
      }
      event_organizer_profiles: {
        Row: {
          approved_at: string | null
          approved_by: string | null
          bio: string | null
          business_name: string | null
          business_type: string | null
          contact_email: string | null
          contact_phone: string | null
          created_at: string | null
          id: string
          is_active: boolean | null
          organizer_name: string
          previous_events_description: string | null
          social_links: Json | null
          updated_at: string | null
          verification_documents: Json | null
          verification_status:
            | Database["public"]["Enums"]["verification_status"]
            | null
          website_url: string | null
          years_of_experience: number | null
        }
        Insert: {
          approved_at?: string | null
          approved_by?: string | null
          bio?: string | null
          business_name?: string | null
          business_type?: string | null
          contact_email?: string | null
          contact_phone?: string | null
          created_at?: string | null
          id: string
          is_active?: boolean | null
          organizer_name: string
          previous_events_description?: string | null
          social_links?: Json | null
          updated_at?: string | null
          verification_documents?: Json | null
          verification_status?:
            | Database["public"]["Enums"]["verification_status"]
            | null
          website_url?: string | null
          years_of_experience?: number | null
        }
        Update: {
          approved_at?: string | null
          approved_by?: string | null
          bio?: string | null
          business_name?: string | null
          business_type?: string | null
          contact_email?: string | null
          contact_phone?: string | null
          created_at?: string | null
          id?: string
          is_active?: boolean | null
          organizer_name?: string
          previous_events_description?: string | null
          social_links?: Json | null
          updated_at?: string | null
          verification_documents?: Json | null
          verification_status?:
            | Database["public"]["Enums"]["verification_status"]
            | null
          website_url?: string | null
          years_of_experience?: number | null
        }
        Relationships: []
      }
      login_attempts: {
        Row: {
          created_at: string | null
          email: string
          failure_reason: string | null
          id: string
          ip_address: unknown | null
          success: boolean
          user_agent: string | null
        }
        Insert: {
          created_at?: string | null
          email: string
          failure_reason?: string | null
          id: string
          ip_address?: unknown | null
          success?: boolean
          user_agent?: string | null
        }
        Update: {
          created_at?: string | null
          email?: string
          failure_reason?: string | null
          id?: string
          ip_address?: unknown | null
          success?: boolean
          user_agent?: string | null
        }
        Relationships: []
      }
      user_profiles: {
        Row: {
          age_visible: boolean | null
          avatar_url: string | null
          bio: string | null
          body_type: string | null
          city: string | null
          country: string | null
          cover_photo_url: string | null
          created_at: string | null
          date_of_birth: string
          display_name: string
          ethnicity: string[] | null
          gender: Database["public"]["Enums"]["gender"] | null
          height_cm: number | null
          id: string
          interests: string[] | null
          is_verified: boolean | null
          kinks: string[] | null
          last_active_visible: boolean | null
          limits: string[] | null
          location: unknown | null
          location_privacy: Database["public"]["Enums"]["privacy_level"] | null
          location_visible: boolean | null
          looking_for: string[] | null
          max_age: number | null
          min_age: number | null
          photo_privacy: Database["public"]["Enums"]["privacy_level"] | null
          photo_urls: string[] | null
          profile_privacy: Database["public"]["Enums"]["privacy_level"] | null
          relationship_status:
            | Database["public"]["Enums"]["relationship_status"]
            | null
          search_distance_km: number | null
          state_province: string | null
          updated_at: string | null
          username: string | null
        }
        Insert: {
          age_visible?: boolean | null
          avatar_url?: string | null
          bio?: string | null
          body_type?: string | null
          city?: string | null
          country?: string | null
          cover_photo_url?: string | null
          created_at?: string | null
          date_of_birth: string
          display_name: string
          ethnicity?: string[] | null
          gender?: Database["public"]["Enums"]["gender"] | null
          height_cm?: number | null
          id: string
          interests?: string[] | null
          is_verified?: boolean | null
          kinks?: string[] | null
          last_active_visible?: boolean | null
          limits?: string[] | null
          location?: unknown | null
          location_privacy?: Database["public"]["Enums"]["privacy_level"] | null
          location_visible?: boolean | null
          looking_for?: string[] | null
          max_age?: number | null
          min_age?: number | null
          photo_privacy?: Database["public"]["Enums"]["privacy_level"] | null
          photo_urls?: string[] | null
          profile_privacy?: Database["public"]["Enums"]["privacy_level"] | null
          relationship_status?:
            | Database["public"]["Enums"]["relationship_status"]
            | null
          search_distance_km?: number | null
          state_province?: string | null
          updated_at?: string | null
          username?: string | null
        }
        Update: {
          age_visible?: boolean | null
          avatar_url?: string | null
          bio?: string | null
          body_type?: string | null
          city?: string | null
          country?: string | null
          cover_photo_url?: string | null
          created_at?: string | null
          date_of_birth?: string
          display_name?: string
          ethnicity?: string[] | null
          gender?: Database["public"]["Enums"]["gender"] | null
          height_cm?: number | null
          id?: string
          interests?: string[] | null
          is_verified?: boolean | null
          kinks?: string[] | null
          last_active_visible?: boolean | null
          limits?: string[] | null
          location?: unknown | null
          location_privacy?: Database["public"]["Enums"]["privacy_level"] | null
          location_visible?: boolean | null
          looking_for?: string[] | null
          max_age?: number | null
          min_age?: number | null
          photo_privacy?: Database["public"]["Enums"]["privacy_level"] | null
          photo_urls?: string[] | null
          profile_privacy?: Database["public"]["Enums"]["privacy_level"] | null
          relationship_status?:
            | Database["public"]["Enums"]["relationship_status"]
            | null
          search_distance_km?: number | null
          state_province?: string | null
          updated_at?: string | null
          username?: string | null
        }
        Relationships: []
      }
      user_roles: {
        Row: {
          created_at: string | null
          expires_at: string | null
          granted_at: string | null
          granted_by: string | null
          id: string
          is_active: boolean | null
          metadata: Json | null
          permissions: Json | null
          role_name: string
          updated_at: string | null
        }
        Insert: {
          created_at?: string | null
          expires_at?: string | null
          granted_at?: string | null
          granted_by?: string | null
          id: string
          is_active?: boolean | null
          metadata?: Json | null
          permissions?: Json | null
          role_name: string
          updated_at?: string | null
        }
        Update: {
          created_at?: string | null
          expires_at?: string | null
          granted_at?: string | null
          granted_by?: string | null
          id?: string
          is_active?: boolean | null
          metadata?: Json | null
          permissions?: Json | null
          role_name?: string
          updated_at?: string | null
        }
        Relationships: []
      }
      user_sessions: {
        Row: {
          browser: string | null
          device_id: string | null
          device_type: string | null
          ended_at: string | null
          id: string
          ip_address: unknown | null
          last_activity_at: string | null
          location: unknown | null
          os: string | null
          started_at: string | null
          user_agent: string | null
        }
        Insert: {
          browser?: string | null
          device_id?: string | null
          device_type?: string | null
          ended_at?: string | null
          id: string
          ip_address?: unknown | null
          last_activity_at?: string | null
          location?: unknown | null
          os?: string | null
          started_at?: string | null
          user_agent?: string | null
        }
        Update: {
          browser?: string | null
          device_id?: string | null
          device_type?: string | null
          ended_at?: string | null
          id?: string
          ip_address?: unknown | null
          last_activity_at?: string | null
          location?: unknown | null
          os?: string | null
          started_at?: string | null
          user_agent?: string | null
        }
        Relationships: []
      }
      user_role_assignments: {
        Row: {
          id: string
          user_id: string
          role_id: string
          assigned_at: string
          assigned_by: string | null
          expires_at: string | null
          is_active: boolean
          metadata: any
        }
        Insert: Omit<Database['public']['Tables']['user_role_assignments']['Row'], 'id' | 'assigned_at'>
        Update: Partial<Database['public']['Tables']['user_role_assignments']['Insert']>
      }
      user_permission_overrides: {
        Row: {
          id: string
          user_id: string
          permission_id: string
          is_granted: boolean
          reason: string | null
          granted_at: string
          granted_by: string | null
          expires_at: string | null
        }
        Insert: Omit<Database['public']['Tables']['user_permission_overrides']['Row'], 'id' | 'granted_at'>
        Update: Partial<Database['public']['Tables']['user_permission_overrides']['Insert']>
      }

      couples: {
        Row: {
          id: string
          primary_user_id: string
          secondary_user_id: string
          couple_name: string
          relationship_start_date: string | null
          bio: string | null
          looking_for: string[] | null
          interests: string[] | null
          kinks: string[] | null
          limits: string[] | null
          profile_privacy: 'public' | 'friends' | 'verified_only' | 'private' | null
          both_must_approve: boolean | null
          is_active: boolean | null
          created_at: string | null
          updated_at: string | null
        }
        Insert: {
          id?: string
          primary_user_id: string
          secondary_user_id: string
          couple_name: string
          relationship_start_date?: string | null
          bio?: string | null
          looking_for?: string[] | null
          interests?: string[] | null
          kinks?: string[] | null
          limits?: string[] | null
          profile_privacy?: 'public' | 'friends' | 'verified_only' | 'private' | null
          both_must_approve?: boolean | null
          is_active?: boolean | null
          created_at?: string | null
          updated_at?: string | null
        }
        Update: {
          id?: string
          primary_user_id?: string
          secondary_user_id?: string
          couple_name?: string
          relationship_start_date?: string | null
          bio?: string | null
          looking_for?: string[] | null
          interests?: string[] | null
          kinks?: string[] | null
          limits?: string[] | null
          profile_privacy?: 'public' | 'friends' | 'verified_only' | 'private' | null
          both_must_approve?: boolean | null
          is_active?: boolean | null
          created_at?: string | null
          updated_at?: string | null
        }
      }
      age_verifications: {
        Row: {
          id: string
          user_id: string
          verification_method: string
          document_type: string | null
          verification_provider: string | null
          verification_data: any | null
          status: 'unverified' | 'pending' | 'verified' | 'rejected' | null
          verified_age: number | null
          verified_at: string | null
          expires_at: string | null
          created_at: string | null
          reviewed_by: string | null
          review_notes: string | null
          rejection_reason: string | null
        }
        Insert: {
          id?: string
          user_id: string
          verification_method: string
          document_type?: string | null
          verification_provider?: string | null
          verification_data?: any | null
          status?: 'unverified' | 'pending' | 'verified' | 'rejected' | null
          verified_age?: number | null
          verified_at?: string | null
          expires_at?: string | null
          created_at?: string | null
          reviewed_by?: string | null
          review_notes?: string | null
          rejection_reason?: string | null
        }
        Update: {
          id?: string
          user_id?: string
          verification_method?: string
          document_type?: string | null
          verification_provider?: string | null
          verification_data?: any | null
          status?: 'unverified' | 'pending' | 'verified' | 'rejected' | null
          verified_age?: number | null
          verified_at?: string | null
          expires_at?: string | null
          created_at?: string | null
          reviewed_by?: string | null
          review_notes?: string | null
          rejection_reason?: string | null
        }
      }
      events: {
        Row: {
          id: string
          title: string
          description: string
          short_description: string
          category: string
          tags: string[]
          event_date: string
          start_time: string
          end_time: string | null
          timezone: string
          venue_type: 'physical' | 'virtual' | 'hybrid'
          venue_name: string | null
          address: string | null
          city: string | null
          country: string | null
          coordinates: any | null
          virtual_link: string | null
          capacity: number
          tickets_sold: number
          status: 'draft' | 'published' | 'cancelled' | 'completed'
          content_rating: 'general' | 'mature' | 'explicit'
          age_restriction: number
          verification_required: boolean
          members_only: boolean
          dresscode: string | null
          rules: string[] | null
          organizer_id: string
          organizer_profile: 'personal' | 'business'
          organizer_name: string
          organizer_bio: string | null
          organizer_contact: string | null
          co_organizers: string[] | null
          main_image_url: string | null
          additional_images: string[] | null
          is_private: boolean
          requires_approval: boolean
          refund_policy: string
          cancellation_policy: string
          special_instructions: string | null
          created_at: string
          updated_at: string
          published_at: string | null
        }
        Insert: {
          id?: string
          title: string
          description: string
          short_description: string
          category: string
          tags?: string[]
          event_date: string
          start_time: string
          end_time?: string | null
          timezone?: string
          venue_type: 'physical' | 'virtual' | 'hybrid'
          venue_name?: string | null
          address?: string | null
          city?: string | null
          country?: string | null
          coordinates?: any | null
          virtual_link?: string | null
          capacity: number
          tickets_sold?: number
          status?: 'draft' | 'published' | 'cancelled' | 'completed'
          content_rating?: 'general' | 'mature' | 'explicit'
          age_restriction?: number
          verification_required?: boolean
          members_only?: boolean
          dresscode?: string | null
          rules?: string[] | null
          organizer_id: string
          organizer_profile?: 'personal' | 'business'
          organizer_name: string
          organizer_bio?: string | null
          organizer_contact?: string | null
          co_organizers?: string[] | null
          main_image_url?: string | null
          additional_images?: string[] | null
          is_private?: boolean
          requires_approval?: boolean
          refund_policy?: string
          cancellation_policy?: string
          special_instructions?: string | null
          created_at?: string
          updated_at?: string
          published_at?: string | null
        }
        Update: {
          id?: string
          title?: string
          description?: string
          short_description?: string
          category?: string
          tags?: string[]
          event_date?: string
          start_time?: string
          end_time?: string | null
          timezone?: string
          venue_type?: 'physical' | 'virtual' | 'hybrid'
          venue_name?: string | null
          address?: string | null
          city?: string | null
          country?: string | null
          coordinates?: any | null
          virtual_link?: string | null
          capacity?: number
          tickets_sold?: number
          status?: 'draft' | 'published' | 'cancelled' | 'completed'
          content_rating?: 'general' | 'mature' | 'explicit'
          age_restriction?: number
          verification_required?: boolean
          members_only?: boolean
          dresscode?: string | null
          rules?: string[] | null
          organizer_id?: string
          organizer_profile?: 'personal' | 'business'
          organizer_name?: string
          organizer_bio?: string | null
          organizer_contact?: string | null
          co_organizers?: string[] | null
          main_image_url?: string | null
          additional_images?: string[] | null
          is_private?: boolean
          requires_approval?: boolean
          refund_policy?: string
          cancellation_policy?: string
          special_instructions?: string | null
          created_at?: string
          updated_at?: string
          published_at?: string | null
        }
      }
      ticket_types: {
        Row: {
          id: string
          event_id: string
          name: string
          description: string
          price: number
          original_price: number | null
          quantity: number
          max_per_person: number
          benefits: string[]
          is_active: boolean
          early_bird_date: string | null
          early_bird_price: number | null
          sale_start_date: string | null
          sale_end_date: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          event_id: string
          name: string
          description: string
          price: number
          original_price?: number | null
          quantity: number
          max_per_person?: number
          benefits?: string[]
          is_active?: boolean
          early_bird_date?: string | null
          early_bird_price?: number | null
          sale_start_date?: string | null
          sale_end_date?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          event_id?: string
          name?: string
          description?: string
          price?: number
          original_price?: number | null
          quantity?: number
          max_per_person?: number
          benefits?: string[]
          is_active?: boolean
          early_bird_date?: string | null
          early_bird_price?: number | null
          sale_start_date?: string | null
          sale_end_date?: string | null
          created_at?: string
          updated_at?: string
        }
      }
      event_bookings: {
        Row: {
          id: string
          event_id: string
          user_id: string
          ticket_type_id: string
          quantity: number
          total_price: number
          attendee_names: string[]
          attendee_emails: string[]
          special_requests: string | null
          payment_status: 'pending' | 'completed' | 'failed' | 'refunded'
          payment_method: string | null
          payment_intent_id: string | null
          booking_status: 'confirmed' | 'cancelled' | 'waitlist' | 'checked_in'
          confirmation_code: string
          qr_code: string | null
          voucher_code: string | null
          discount_amount: number | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          event_id: string
          user_id: string
          ticket_type_id: string
          quantity: number
          total_price: number
          attendee_names?: string[]
          attendee_emails?: string[]
          special_requests?: string | null
          payment_status?: 'pending' | 'completed' | 'failed' | 'refunded'
          payment_method?: string | null
          payment_intent_id?: string | null
          booking_status?: 'confirmed' | 'cancelled' | 'waitlist' | 'checked_in'
          confirmation_code: string
          qr_code?: string | null
          voucher_code?: string | null
          discount_amount?: number | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          event_id?: string
          user_id?: string
          ticket_type_id?: string
          quantity?: number
          total_price?: number
          attendee_names?: string[]
          attendee_emails?: string[]
          special_requests?: string | null
          payment_status?: 'pending' | 'completed' | 'failed' | 'refunded'
          payment_method?: string | null
          payment_intent_id?: string | null
          booking_status?: 'confirmed' | 'cancelled' | 'waitlist' | 'checked_in'
          confirmation_code?: string
          qr_code?: string | null
          voucher_code?: string | null
          discount_amount?: number | null
          created_at?: string
          updated_at?: string
        }
      }
      event_organizer_accounts: {
        Row: {
          id: string
          user_id: string
          business_name: string | null
          display_name: string
          bio: string
          contact_email: string
          phone: string | null
          website: string | null
          is_verified: boolean
          verification_date: string | null
          auto_approve_events: boolean
          default_refund_policy: string
          commission_rate: number
          analytics_access: boolean
          admin_users: string[] | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          user_id: string
          business_name?: string | null
          display_name: string
          bio: string
          contact_email: string
          phone?: string | null
          website?: string | null
          is_verified?: boolean
          verification_date?: string | null
          auto_approve_events?: boolean
          default_refund_policy?: string
          commission_rate?: number
          analytics_access?: boolean
          admin_users?: string[] | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          user_id?: string
          business_name?: string | null
          display_name?: string
          bio?: string
          contact_email?: string
          phone?: string | null
          website?: string | null
          is_verified?: boolean
          verification_date?: string | null
          auto_approve_events?: boolean
          default_refund_policy?: string
          commission_rate?: number
          analytics_access?: boolean
          admin_users?: string[] | null
          created_at?: string
          updated_at?: string
        }
      }
      voucher_codes: {
        Row: {
          id: string
          code: string
          event_id: string | null
          organizer_id: string | null
          discount_type: 'percentage' | 'fixed_amount' | 'free'
          discount_value: number
          max_uses: number
          used_count: number
          valid_from: string
          valid_until: string
          is_active: boolean
          min_purchase_amount: number | null
          applicable_ticket_types: string[] | null
          first_time_buyers_only: boolean | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          code: string
          event_id?: string | null
          organizer_id?: string | null
          discount_type: 'percentage' | 'fixed_amount' | 'free'
          discount_value: number
          max_uses: number
          used_count?: number
          valid_from: string
          valid_until: string
          is_active?: boolean
          min_purchase_amount?: number | null
          applicable_ticket_types?: string[] | null
          first_time_buyers_only?: boolean | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          code?: string
          event_id?: string | null
          organizer_id?: string | null
          discount_type?: 'percentage' | 'fixed_amount' | 'free'
          discount_value?: number
          max_uses?: number
          used_count?: number
          valid_from?: string
          valid_until?: string
          is_active?: boolean
          min_purchase_amount?: number | null
          applicable_ticket_types?: string[] | null
          first_time_buyers_only?: boolean | null
          created_at?: string
          updated_at?: string
        }
      }
      invite_codes: {
        Row: {
          id: string
          code: string
          created_by_id: string
          creator_type: 'user' | 'ambassador' | 'admin'
          max_uses: number
          current_uses: number
          is_active: boolean
          expires_at: string | null
          valid_from: string
          allowed_domains: string[] | null
          required_age_min: number | null
          geographic_restrictions: string[] | null
          metadata: any | null
          created_at: string
          updated_at: string
          last_used_at: string | null
        }
        Insert: {
          id?: string
          code: string
          created_by_id: string
          creator_type: 'user' | 'ambassador' | 'admin'
          max_uses: number
          current_uses?: number
          is_active?: boolean
          expires_at?: string | null
          valid_from: string
          allowed_domains?: string[] | null
          required_age_min?: number | null
          geographic_restrictions?: string[] | null
          metadata?: any | null
          created_at?: string
          updated_at?: string
          last_used_at?: string | null
        }
        Update: {
          id?: string
          code?: string
          created_by_id?: string
          creator_type?: 'user' | 'ambassador' | 'admin'
          max_uses?: number
          current_uses?: number
          is_active?: boolean
          expires_at?: string | null
          valid_from?: string
          allowed_domains?: string[] | null
          required_age_min?: number | null
          geographic_restrictions?: string[] | null
          metadata?: any | null
          created_at?: string
          updated_at?: string
          last_used_at?: string | null
        }
      }
      invite_code_usage: {
        Row: {
          id: string
          invite_code_id: string
          used_by_email: string
          used_by_user_id: string | null
          registration_completed: boolean
          registration_completed_at: string | null
          subscription_converted: boolean
          subscription_converted_at: string | null
          ip_address: string | null
          user_agent: string | null
          referrer_url: string | null
          used_at: string
          verified_at: string | null
        }
        Insert: {
          id?: string
          invite_code_id: string
          used_by_email: string
          used_by_user_id?: string | null
          registration_completed?: boolean
          registration_completed_at?: string | null
          subscription_converted?: boolean
          subscription_converted_at?: string | null
          ip_address?: string | null
          user_agent?: string | null
          referrer_url?: string | null
          used_at?: string
          verified_at?: string | null
        }
        Update: {
          id?: string
          invite_code_id?: string
          used_by_email?: string
          used_by_user_id?: string | null
          registration_completed?: boolean
          registration_completed_at?: string | null
          subscription_converted?: boolean
          subscription_converted_at?: string | null
          ip_address?: string | null
          user_agent?: string | null
          referrer_url?: string | null
          used_at?: string
          verified_at?: string | null
        }
      }
      waiting_list: {
        Row: {
          id: string
          email: string
          display_name: string | null
          interested_reason: string | null
          referred_by: string | null
          status: 'waiting' | 'invited' | 'registered' | 'expired'
          priority: 'low' | 'medium' | 'high' | 'urgent'
          source: string | null
          campaign: string | null
          metadata: any | null
          notified_at: string | null
          invite_code_sent: string | null
          joined_at: string
          invited_at: string | null
          registered_at: string | null
        }
        Insert: {
          id?: string
          email: string
          display_name?: string | null
          interested_reason?: string | null
          referred_by?: string | null
          status?: 'waiting' | 'invited' | 'registered' | 'expired'
          priority?: 'low' | 'medium' | 'high' | 'urgent'
          source?: string | null
          campaign?: string | null
          metadata?: any | null
          notified_at?: string | null
          invite_code_sent?: string | null
          joined_at?: string
          invited_at?: string | null
          registered_at?: string | null
        }
        Update: {
          id?: string
          email?: string
          display_name?: string | null
          interested_reason?: string | null
          referred_by?: string | null
          status?: 'waiting' | 'invited' | 'registered' | 'expired'
          priority?: 'low' | 'medium' | 'high' | 'urgent'
          source?: string | null
          campaign?: string | null
          metadata?: any | null
          notified_at?: string | null
          invite_code_sent?: string | null
          joined_at?: string
          invited_at?: string | null
          registered_at?: string | null
        }
      }
      subscription_plans: {
        Row: {
          id: string
          name: string
          tier: 'basic' | 'gold' | 'platinum' | 'ambassador'
          description: string
          price: number
          currency: string
          billing_interval: 'monthly' | 'yearly' | 'lifetime'
          features: any[]
          limits: any
          badge_color: string
          badge_icon: string
          status_text: string
          exclusive_features: string[]
          is_active: boolean
          is_visible: boolean
          sort_order: number
          trial_days: number | null
          discount_percentage: number | null
          promo_text: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          name: string
          tier: 'basic' | 'gold' | 'platinum' | 'ambassador'
          description: string
          price: number
          currency: string
          billing_interval: 'monthly' | 'yearly' | 'lifetime'
          features: any[]
          limits: any
          badge_color: string
          badge_icon: string
          status_text: string
          exclusive_features: string[]
          is_active?: boolean
          is_visible?: boolean
          sort_order?: number
          trial_days?: number | null
          discount_percentage?: number | null
          promo_text?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          name?: string
          tier?: 'basic' | 'gold' | 'platinum' | 'ambassador'
          description?: string
          price?: number
          currency?: string
          billing_interval?: 'monthly' | 'yearly' | 'lifetime'
          features?: any[]
          limits?: any
          badge_color?: string
          badge_icon?: string
          status_text?: string
          exclusive_features?: string[]
          is_active?: boolean
          is_visible?: boolean
          sort_order?: number
          trial_days?: number | null
          discount_percentage?: number | null
          promo_text?: string | null
          created_at?: string
          updated_at?: string
        }
      }
      user_subscriptions: {
        Row: {
          id: string
          user_id: string
          plan_id: string
          status: 'active' | 'inactive' | 'cancelled' | 'expired' | 'paused' | 'pending'
          tier: 'basic' | 'gold' | 'platinum' | 'ambassador'
          current_period_start: string
          current_period_end: string
          next_billing_date: string | null
          amount: number
          currency: string
          payment_method_id: string | null
          stripe_subscription_id: string | null
          last_payment_date: string | null
          last_payment_amount: number | null
          feature_usage: any
          limits_reset: any
          is_grandfathered: boolean
          special_discount: number | null
          promo_code: string | null
          trial_ends_at: string | null
          cancelled_at: string | null
          cancel_at_period_end: boolean
          created_at: string
          updated_at: string
          last_billed_at: string | null
        }
        Insert: {
          id?: string
          user_id: string
          plan_id: string
          status?: 'active' | 'inactive' | 'cancelled' | 'expired' | 'paused' | 'pending'
          tier: 'basic' | 'gold' | 'platinum' | 'ambassador'
          current_period_start: string
          current_period_end: string
          next_billing_date?: string | null
          amount: number
          currency: string
          payment_method_id?: string | null
          stripe_subscription_id?: string | null
          last_payment_date?: string | null
          last_payment_amount?: number | null
          feature_usage?: any
          limits_reset?: any
          is_grandfathered?: boolean
          special_discount?: number | null
          promo_code?: string | null
          trial_ends_at?: string | null
          cancelled_at?: string | null
          cancel_at_period_end?: boolean
          created_at?: string
          updated_at?: string
          last_billed_at?: string | null
        }
        Update: {
          id?: string
          user_id?: string
          plan_id?: string
          status?: 'active' | 'inactive' | 'cancelled' | 'expired' | 'paused' | 'pending'
          tier?: 'basic' | 'gold' | 'platinum' | 'ambassador'
          current_period_start?: string
          current_period_end?: string
          next_billing_date?: string | null
          amount?: number
          currency?: string
          payment_method_id?: string | null
          stripe_subscription_id?: string | null
          last_payment_date?: string | null
          last_payment_amount?: number | null
          feature_usage?: any
          limits_reset?: any
          is_grandfathered?: boolean
          special_discount?: number | null
          promo_code?: string | null
          trial_ends_at?: string | null
          cancelled_at?: string | null
          cancel_at_period_end?: boolean
          created_at?: string
          updated_at?: string
          last_billed_at?: string | null
        }
      }
      ambassador_profiles: {
        Row: {
          id: string
          user_id: string
          status: 'pending' | 'active' | 'suspended' | 'retired'
          tier: 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond'
          bio: string
          specialties: string[]
          languages: string[]
          time_zone: string
          metrics: any
          commission_rate: number
          bonus_eligible: boolean
          total_earnings: number
          pending_payouts: number
          public_profile: boolean
          accepting_invites: boolean
          max_invites_per_month: number
          preferred_contact: 'app' | 'email' | 'both'
          availability_schedule: any | null
          badges: any[]
          achievements: any[]
          applied_at: string
          approved_at: string | null
          activated_at: string | null
          last_active_at: string
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          user_id: string
          status?: 'pending' | 'active' | 'suspended' | 'retired'
          tier?: 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond'
          bio: string
          specialties: string[]
          languages: string[]
          time_zone: string
          metrics?: any
          commission_rate?: number
          bonus_eligible?: boolean
          total_earnings?: number
          pending_payouts?: number
          public_profile?: boolean
          accepting_invites?: boolean
          max_invites_per_month?: number
          preferred_contact?: 'app' | 'email' | 'both'
          availability_schedule?: any | null
          badges?: any[]
          achievements?: any[]
          applied_at: string
          approved_at?: string | null
          activated_at?: string | null
          last_active_at?: string
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          user_id?: string
          status?: 'pending' | 'active' | 'suspended' | 'retired'
          tier?: 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond'
          bio?: string
          specialties?: string[]
          languages?: string[]
          time_zone?: string
          metrics?: any
          commission_rate?: number
          bonus_eligible?: boolean
          total_earnings?: number
          pending_payouts?: number
          public_profile?: boolean
          accepting_invites?: boolean
          max_invites_per_month?: number
          preferred_contact?: 'app' | 'email' | 'both'
          availability_schedule?: any | null
          badges?: any[]
          achievements?: any[]
          applied_at?: string
          approved_at?: string | null
          activated_at?: string | null
          last_active_at?: string
          created_at?: string
          updated_at?: string
        }
      }
      referral_codes: {
        Row: {
          id: string
          code: string
          owner_id: string
          owner_type: 'user' | 'ambassador'
          commission_rate: number
          cookie_duration: number
          is_active: boolean
          total_clicks: number
          total_conversions: number
          total_revenue: number
          analytics: any
          created_at: string
          updated_at: string
          last_used_at: string | null
        }
        Insert: {
          id?: string
          code: string
          owner_id: string
          owner_type: 'user' | 'ambassador'
          commission_rate: number
          cookie_duration?: number
          is_active?: boolean
          total_clicks?: number
          total_conversions?: number
          total_revenue?: number
          analytics?: any
          created_at?: string
          updated_at?: string
          last_used_at?: string | null
        }
        Update: {
          id?: string
          code?: string
          owner_id?: string
          owner_type?: 'user' | 'ambassador'
          commission_rate?: number
          cookie_duration?: number
          is_active?: boolean
          total_clicks?: number
          total_conversions?: number
          total_revenue?: number
          analytics?: any
          created_at?: string
          updated_at?: string
          last_used_at?: string | null
        }
      }
      posts: {
        Row: {
          id: string
          user_id: string
          content: string | null
          media_urls: string[]
          media_types: string[]
          location_name: string | null
          location_coordinates: any | null
          visibility: 'public' | 'friends' | 'private'
          tags: string[]
          event_id: string | null
          is_adult_content: boolean
          content_rating: 'safe' | 'suggestive' | 'explicit' | 'adult'
          allow_comments: boolean
          allow_sharing: boolean
          scheduled_at: string | null
          published_at: string | null
          edited_at: string | null
          created_at: string
          updated_at: string
          likes_count: number
          comments_count: number
          shares_count: number
          saves_count: number
          views_count: number
          is_flagged: boolean
          moderation_status: 'pending' | 'approved' | 'rejected' | 'under_review'
          moderation_reason: string | null
          moderated_at: string | null
          moderated_by: string | null
        }
        Insert: {
          id?: string
          user_id: string
          content?: string | null
          media_urls?: string[]
          media_types?: string[]
          location_name?: string | null
          location_coordinates?: any | null
          visibility?: 'public' | 'friends' | 'private'
          tags?: string[]
          event_id?: string | null
          is_adult_content?: boolean
          content_rating?: 'safe' | 'suggestive' | 'explicit' | 'adult'
          allow_comments?: boolean
          allow_sharing?: boolean
          scheduled_at?: string | null
          published_at?: string | null
          edited_at?: string | null
          created_at?: string
          updated_at?: string
          likes_count?: number
          comments_count?: number
          shares_count?: number
          saves_count?: number
          views_count?: number
          is_flagged?: boolean
          moderation_status?: 'pending' | 'approved' | 'rejected' | 'under_review'
          moderation_reason?: string | null
          moderated_at?: string | null
          moderated_by?: string | null
        }
        Update: {
          id?: string
          user_id?: string
          content?: string | null
          media_urls?: string[]
          media_types?: string[]
          location_name?: string | null
          location_coordinates?: any | null
          visibility?: 'public' | 'friends' | 'private'
          tags?: string[]
          event_id?: string | null
          is_adult_content?: boolean
          content_rating?: 'safe' | 'suggestive' | 'explicit' | 'adult'
          allow_comments?: boolean
          allow_sharing?: boolean
          scheduled_at?: string | null
          published_at?: string | null
          edited_at?: string | null
          created_at?: string
          updated_at?: string
          likes_count?: number
          comments_count?: number
          shares_count?: number
          saves_count?: number
          views_count?: number
          is_flagged?: boolean
          moderation_status?: 'pending' | 'approved' | 'rejected' | 'under_review'
          moderation_reason?: string | null
          moderated_at?: string | null
          moderated_by?: string | null
        }
      }
      post_comments: {
        Row: {
          id: string
          post_id: string
          user_id: string
          parent_comment_id: string | null
          content: string
          media_urls: string[]
          media_types: string[]
          is_edited: boolean
          edited_at: string | null
          created_at: string
          updated_at: string
          likes_count: number
          replies_count: number
          is_flagged: boolean
          moderation_status: 'pending' | 'approved' | 'rejected'
        }
        Insert: {
          id?: string
          post_id: string
          user_id: string
          parent_comment_id?: string | null
          content: string
          media_urls?: string[]
          media_types?: string[]
          is_edited?: boolean
          edited_at?: string | null
          created_at?: string
          updated_at?: string
          likes_count?: number
          replies_count?: number
          is_flagged?: boolean
          moderation_status?: 'pending' | 'approved' | 'rejected'
        }
        Update: {
          id?: string
          post_id?: string
          user_id?: string
          parent_comment_id?: string | null
          content?: string
          media_urls?: string[]
          media_types?: string[]
          is_edited?: boolean
          edited_at?: string | null
          created_at?: string
          updated_at?: string
          likes_count?: number
          replies_count?: number
          is_flagged?: boolean
          moderation_status?: 'pending' | 'approved' | 'rejected'
        }
      }
      post_likes: {
        Row: {
          id: string
          post_id: string
          user_id: string
          created_at: string
        }
        Insert: {
          id?: string
          post_id: string
          user_id: string
          created_at?: string
        }
        Update: {
          id?: string
          post_id?: string
          user_id?: string
          created_at?: string
        }
      }
      comment_likes: {
        Row: {
          id: string
          comment_id: string
          user_id: string
          created_at: string
        }
        Insert: {
          id?: string
          comment_id: string
          user_id: string
          created_at?: string
        }
        Update: {
          id?: string
          comment_id?: string
          user_id?: string
          created_at?: string
        }
      }
      post_saves: {
        Row: {
          id: string
          post_id: string
          user_id: string
          collection_name: string
          created_at: string
        }
        Insert: {
          id?: string
          post_id: string
          user_id: string
          collection_name?: string
          created_at?: string
        }
        Update: {
          id?: string
          post_id?: string
          user_id?: string
          collection_name?: string
          created_at?: string
        }
      }
      post_shares: {
        Row: {
          id: string
          post_id: string
          user_id: string
          share_type: 'repost' | 'quote' | 'direct' | 'external'
          message: string | null
          platform: string | null
          created_at: string
        }
        Insert: {
          id?: string
          post_id: string
          user_id: string
          share_type: 'repost' | 'quote' | 'direct' | 'external'
          message?: string | null
          platform?: string | null
          created_at?: string
        }
        Update: {
          id?: string
          post_id?: string
          user_id?: string
          share_type?: 'repost' | 'quote' | 'direct' | 'external'
          message?: string | null
          platform?: string | null
          created_at?: string
        }
      }
      user_follows: {
        Row: {
          id: string
          follower_id: string
          following_id: string
          status: 'active' | 'pending' | 'blocked'
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          follower_id: string
          following_id: string
          status?: 'active' | 'pending' | 'blocked'
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          follower_id?: string
          following_id?: string
          status?: 'active' | 'pending' | 'blocked'
          created_at?: string
          updated_at?: string
        }
      }
      hashtags: {
        Row: {
          id: string
          tag: string
          normalized_tag: string
          usage_count: number
          is_trending: boolean
          category: string | null
          is_adult: boolean
          created_at: string
          last_used_at: string
        }
        Insert: {
          id?: string
          tag: string
          normalized_tag: string
          usage_count?: number
          is_trending?: boolean
          category?: string | null
          is_adult?: boolean
          created_at?: string
          last_used_at?: string
        }
        Update: {
          id?: string
          tag?: string
          normalized_tag?: string
          usage_count?: number
          is_trending?: boolean
          category?: string | null
          is_adult?: boolean
          created_at?: string
          last_used_at?: string
        }
      }
      post_hashtags: {
        Row: {
          id: string
          post_id: string
          hashtag_id: string
          created_at: string
        }
        Insert: {
          id?: string
          post_id: string
          hashtag_id: string
          created_at?: string
        }
        Update: {
          id?: string
          post_id?: string
          hashtag_id?: string
          created_at?: string
        }
      }
      user_blocks: {
        Row: {
          id: string
          blocker_id: string
          blocked_id: string
          reason: string | null
          created_at: string
        }
        Insert: {
          id?: string
          blocker_id: string
          blocked_id: string
          reason?: string | null
          created_at?: string
        }
        Update: {
          id?: string
          blocker_id?: string
          blocked_id?: string
          reason?: string | null
          created_at?: string
        }
      }
      content_reports: {
        Row: {
          id: string
          reporter_id: string
          content_type: 'post' | 'comment' | 'profile' | 'message'
          content_id: string
          reason: 'spam' | 'harassment' | 'inappropriate' | 'copyright' | 'adult_content' | 'violence' | 'hate_speech' | 'fake_news' | 'other'
          description: string | null
          evidence_urls: string[]
          status: 'pending' | 'investigating' | 'resolved' | 'dismissed'
          resolution: string | null
          resolved_by: string | null
          resolved_at: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          reporter_id: string
          content_type: 'post' | 'comment' | 'profile' | 'message'
          content_id: string
          reason: 'spam' | 'harassment' | 'inappropriate' | 'copyright' | 'adult_content' | 'violence' | 'hate_speech' | 'fake_news' | 'other'
          description?: string | null
          evidence_urls?: string[]
          status?: 'pending' | 'investigating' | 'resolved' | 'dismissed'
          resolution?: string | null
          resolved_by?: string | null
          resolved_at?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          reporter_id?: string
          content_type?: 'post' | 'comment' | 'profile' | 'message'
          content_id?: string
          reason?: 'spam' | 'harassment' | 'inappropriate' | 'copyright' | 'adult_content' | 'violence' | 'hate_speech' | 'fake_news' | 'other'
          description?: string | null
          evidence_urls?: string[]
          status?: 'pending' | 'investigating' | 'resolved' | 'dismissed'
          resolution?: string | null
          resolved_by?: string | null
          resolved_at?: string | null
          created_at?: string
          updated_at?: string
        }
      }
    }
    Views: {
      [_ in never]: never
    }
    Functions: {
      [_ in never]: never
    }
    Enums: {
      account_type: 'individual' | 'couple'
      gender: 'male' | 'female' | 'non_binary' | 'trans_male' | 'trans_female' | 'other'
      privacy_level: 'public' | 'friends' | 'verified_only' | 'private'
      relationship_status: 'single' | 'in_relationship' | 'married' | 'divorced' | 'widowed' | 'complicated'
      verification_status: 'unverified' | 'pending' | 'verified' | 'rejected'
      content_rating: 'safe' | 'suggestive' | 'explicit' | 'adult'
      post_visibility: 'public' | 'friends' | 'private'
      moderation_status: 'pending' | 'approved' | 'rejected' | 'under_review'
      follow_status: 'active' | 'pending' | 'blocked'
      report_reason: 'spam' | 'harassment' | 'inappropriate' | 'copyright' | 'adult_content' | 'violence' | 'hate_speech' | 'fake_news' | 'other'
    }
  }
}

// Enhanced auth types for RBAC system
export type AuthUser = {
  id: string
  email: string
  user_metadata: {
    username?: string
    display_name?: string
    account_type?: 'individual' | 'couple'
    age_verified?: boolean
    terms_accepted?: boolean
    privacy_policy_accepted?: boolean
    avatar_url?: string
  }
  app_metadata: {
    provider?: string
    providers?: string[]
    role?: string
    roles?: string[]
  }
  created_at: string
  updated_at: string
  last_sign_in_at?: string
  email_confirmed_at?: string
  // Additional computed properties for UI consistency
  avatar?: string
  displayName?: string
  username?: string
  role?: UserRole
  isPremium?: boolean
  organizerStatus?: OrganizerStatus
}

// Role and permission types
export type UserRole = Database['public']['Tables']['user_roles']['Row']

export type UserRoleAssignment = Database['public']['Tables']['user_role_assignments']['Row']
export type UserPermissionOverride = Database['public']['Tables']['user_permission_overrides']['Row']
export type OrganizerProfile = Database['public']['Tables']['event_organizer_profiles']['Row']
export type AdminActivityLog = Database['public']['Tables']['admin_activity_logs']['Row']
export type UserSession = Database['public']['Tables']['user_sessions']['Row']
export type LoginAttempt = Database['public']['Tables']['login_attempts']['Row']

export type UserProfile = Database['public']['Tables']['user_profiles']['Row']
export type CoupleProfile = Database['public']['Tables']['couples']['Row']
export type AgeVerification = Database['public']['Tables']['age_verifications']['Row']

// Enhanced user profile with roles
export type EnhancedUserProfile = Database['public']['Tables']['user_profiles']['Row'] & {
  roles: UserRole[]
  permissions: string[]
  effectiveRole: UserRole
  organizerProfile?: OrganizerProfile
}

// Permission check helper types
export type PermissionName = 
  // User permissions
  | 'profile:read' | 'profile:write' | 'profile:delete'
  | 'messages:read' | 'messages:write'
  | 'photos:upload' | 'photos:private'
  // Content permissions  
  | 'posts:read' | 'posts:write' | 'posts:delete'
  | 'comments:read' | 'comments:write'
  | 'content:report' | 'content:moderate'
  // Event permissions
  | 'events:read' | 'events:book' | 'events:create' | 'events:manage' | 'events:moderate'
  | 'tickets:scan'
  // Admin permissions
  | 'users:read' | 'users:write' | 'users:ban' | 'users:verify'
  | 'organizers:approve'
  | 'reports:read' | 'reports:resolve'
  | 'analytics:read'
  // System permissions
  | 'system:config' | 'system:maintenance'
  | 'roles:manage' | 'payments:manage'
  | 'data:export' | 'logs:read'

export type RoleName = 'user' | 'event_organizer' | 'admin' | 'super_admin'

// Auth context type
export interface AuthContextType {
  user: AuthUser | null
  profile: EnhancedUserProfile | null
  session: any | null
  isLoading: boolean
  hasPermission: (permission: PermissionName) => boolean
  hasRole: (role: RoleName) => boolean
  getRoleLevel: () => number
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string, profileData: any) => Promise<void>
  signOut: () => Promise<void>
  updateProfile: (updates: Partial<EnhancedUserProfile>) => Promise<void>
  applyForOrganizer: (organizerData: Partial<OrganizerProfile>) => Promise<void>
  refreshUserData: () => Promise<void>
}

// Auth event types for the application
export interface AuthEvents {
  signIn: { user: AuthUser; session: any }
  signOut: { user: AuthUser | null }
  tokenRefreshed: { user: AuthUser; session: any }
  userUpdated: { user: AuthUser }
  passwordRecovery: { user: AuthUser }
}

// Sexual preferences system types
export type PreferenceLevel = 'like' | 'sometimes' | 'dislike' | 'hard_no' | 'want_to_try'

export interface SexualPreference {
  category: string
  subcategory: string
  level: PreferenceLevel
  notes?: string
  private?: boolean
}

export interface SexualPreferencesProfile {
  user_id: string
  preferences: SexualPreference[]
  visibility: 'public' | 'matches' | 'verified_only' | 'private'
  updated_at: string
}